import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, ChevronRight, Clock, Sprout, Package, Smile, BarChart3, Leaf } from "lucide-react";
import { RECIPES, featurePath, M } from "../../data/site";
import { EASE } from "../site/motion";

const ROWS = [
  { icon: Leaf, label: "Healthy meals for your family", to: featurePath("meal-recommendations") },
  { icon: Package, label: "Pantry-based suggestions", to: featurePath("pantry-shopping") },
  { icon: Smile, label: "Kid-friendly options", to: featurePath("guided-cooking") },
  { icon: BarChart3, label: "Nutrition insights", to: featurePath("feedback-memory") },
];

const Label = () => <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.4em] text-slate-200">Concept preview</p>;

export const KitchenPanel = ({ play }) => (
  <motion.div initial={{ opacity: 0, y: 40 }} animate={play ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1.1, ease: EASE, delay: 0.75 }} data-testid="kitchen-companion-panel">
    <Label />
    <div className="glass grid gap-4 rounded-3xl p-4 sm:p-5 md:grid-cols-[1.45fr_1fr]">
      <div>
        <div className="mb-4 flex items-start gap-3">
          <ChefHat className="h-9 w-9 shrink-0 text-cyan-200" strokeWidth={1.4} />
          <div>
            <p className="font-display text-xl font-semibold text-white">Kitchen Companion</p>
            <p className="text-sm text-slate-300">Personalized meal ideas for a healthier home.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {RECIPES.map((r, i) => (
            <Link key={r.name} to={featurePath("meal-recommendations")} data-testid={`hero-recipe-${i}`} className="group overflow-hidden rounded-xl border border-cyan-400/25 bg-deep/60 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-cyan-300">
              <div className="aspect-[4/3] overflow-hidden"><img src={r.img} alt={r.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
              <div className="p-2">
                <p className="line-clamp-2 text-xs font-medium leading-snug text-white">{r.name}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-300"><Clock className="h-3 w-3" />{r.time}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {ROWS.map((r) => (
          <Link key={r.label} to={r.to} data-testid={`hero-option-${r.label.split(" ")[0].toLowerCase()}`} className="group flex flex-1 items-center gap-3 rounded-xl border border-cyan-400/25 bg-deep/60 px-3 py-2.5 transition-[border-color,background-color] duration-300 hover:border-cyan-300 hover:bg-blue-900/30">
            <r.icon className="h-5 w-5 shrink-0 text-cyan-200" strokeWidth={1.5} />
            <span className="flex-1 text-sm text-slate-100">{r.label}</span>
            <ChevronRight className="h-4 w-4 text-slate-300 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </div>
  </motion.div>
);

export const GrowPanel = ({ play }) => (
  <motion.div initial={{ opacity: 0, x: 40 }} animate={play ? { opacity: 1, x: 0 } : {}} transition={{ duration: 1.1, ease: EASE, delay: 0.95 }} style={{ perspective: 1000 }}>
    <Link to="/advanced-learning" data-testid="hero-honestgrow-panel" className="group block transition-transform duration-500 lg:[transform:rotateY(-12deg)_rotateZ(-1deg)] lg:hover:[transform:rotateY(0deg)]">
      <Label />
      <div className="glass rounded-3xl p-3">
        <div className="aspect-[4/3] overflow-hidden rounded-2xl"><img src={M("c_garden.jpg")} alt="Honesti gardening with a child" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
        <div className="flex items-center gap-3 px-2 pb-2 pt-4">
          <Sprout className="h-8 w-8 shrink-0 text-cyan-200" strokeWidth={1.4} />
          <div className="flex-1">
            <p className="font-display text-lg font-semibold text-white">HonestGrow™</p>
            <p className="text-sm text-slate-300">Simple guidance for a stronger, brighter tomorrow.</p>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-200 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  </motion.div>
);
